import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';

import { v4 as uuidv4 } from 'uuid';
import { SeatsioSeatingChart } from '@seatsio/seatsio-react';
import { holdObjectColor, holdObjectIcon, determineChannelKey, getHoldPopoverInfo } from '../../../shared/seatsioUtils';
import usePageColorScheme from '../../shared/hooks/usePageColorScheme';

var formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

var _ = require('lodash');
var debouncedBuildTicketReservation;
var seatsIOObjectSelected;
var seatsIOObjectDeselected;

const ChooseSeatedTicketTypes = ({
  team,
  csrfToken,
  confirm,
  ticketTypes,
  ticketTypesChanged,
  addOns,
  seatsIOPublicKey,
  seatsIOChart,
  seatsIOChartChanged,
  seatsIOSelectedObjects,
  seatsIOSelectedObjectsChanged,
  selectedObjectLabel,
  selectedObjectType,
  buildTicketReservation,
  orderType,
  eventHolds
}) => {
  const colorScheme = usePageColorScheme();

  useEffect(() => {
    debouncedBuildTicketReservation = _.debounce((csrfToken, team, confirm, orderType, updatedTicketTypes, addOns) => {
      buildTicketReservation(csrfToken, team, confirm, orderType, updatedTicketTypes, addOns);
    }, 300);
  }, []);

  const selectionsRef = useRef(seatsIOSelectedObjects);
  useEffect(() => {
    selectionsRef.current = seatsIOSelectedObjects;
  }, [seatsIOSelectedObjects]);

  const pricedCategoryKeys = new Set(
    Object.entries(_.countBy(ticketTypes.filter((tt) => tt.seating_chart_category_key), (tt) => String(tt.seating_chart_category_key)))
      .filter(([, count]) => count > 1)
      .map(([key]) => key)
  );

  useEffect(() => {
    const ticketTypesFor = (selections) => [...ticketTypes].map((tt) => {
      var filteredSelections = selections.filter(({ object, ticketType }) =>
        object.category.key.toString() === tt.seating_chart_category_key
          && (!pricedCategoryKeys.has(tt.seating_chart_category_key) || ticketType === String(tt.id) || (ticketType === null && tt.price_parent_id == null))
      );

      return Object.assign({}, tt, {
        seatsIOObjects: filteredSelections.map(({ object }) => {
          return {
            seatsio_object_id: object.id,
            seat_assignment: selectedObjectLabel(object),
            seat_type: selectedObjectType(object),
            hold_token: object.chart?.holdToken,
            channel_key: determineChannelKey(object, eventHolds?.allChannelKeysIncludingNoChannel || [])
          }
        }),
        quantity: filteredSelections.length
      });
    });

    const selectionChanged = (updatedSelections) => {
      var updatedTicketTypes = ticketTypesFor(updatedSelections);

      selectionsRef.current = updatedSelections;
      seatsIOSelectedObjectsChanged(updatedSelections);
      ticketTypesChanged(updatedTicketTypes);
      debouncedBuildTicketReservation(csrfToken, team, confirm, orderType, updatedTicketTypes, addOns);
    };

    const ticketTypeKey = (object, ticketType) => {
      var key = ticketType?.ticketType ?? object.selectedTicketType;
      return key == null ? null : String(key);
    };

    seatsIOObjectSelected = (object, selectedTicketType) => {
      selectionChanged([...selectionsRef.current, { object, ticketType: ticketTypeKey(object, selectedTicketType) }]);
    };

    seatsIOObjectDeselected = (object, deselectedTicketType) => {
      var ticketType = ticketTypeKey(object, deselectedTicketType);
      var selections = selectionsRef.current;
      var index = selections.findIndex((selection) => selection.object.id === object.id && selection.ticketType === ticketType);
      if(index < 0){
        index = selections.findIndex((selection) => selection.object.id === object.id && (ticketType === null || selection.ticketType === null));
      }

      if(index < 0){
        return;
      }

      selectionChanged(selections.filter((selection, i) => i !== index));
    };
  }, [seatsIOSelectedObjects, csrfToken, confirm, ticketTypes, addOns, team, orderType, eventHolds]);

  const pricing = Object.values(_.groupBy(ticketTypes, (tt) => String(tt.seating_chart_category_key))).flatMap((group) => {
    var category = group[0].seating_chart_category_key;

    if(pricedCategoryKeys.has(String(category))){
      return [{
        category: category,
        ticketTypes: group.map((tt) => ({ ticketType: String(tt.id), label: tt.name, price: parseFloat(tt.price) }))
      }];
    }

    return group.map((tt) => ({ category: tt.seating_chart_category_key, price: parseFloat(tt.price) }));
  });
  const pricingKey = JSON.stringify(pricing);

  useEffect(() => {
    if(Object.keys(seatsIOChart).length > 0){
      seatsIOChart.changeConfig({ pricing: pricing });
    }
  }, [seatsIOChart, pricingKey]);

  return (
    <div>
      {confirm.seatsio_event_id && confirm.seatsio_event_id.length > 0 ? (
        <div className="row">
          <div className="col-12">
            <div className="card"
                 style={{
                   "border": "1px solid var(--od-c-f3f3f3, #f3f3f3)",
                   "height": "515px"
                 }}>
              <SeatsioSeatingChart workspaceKey={seatsIOPublicKey}
                                   extraConfig={{
                                     labelToHoldConfig: eventHolds?.labelToHoldConfig
                                   }}
                                   event={confirm.seatsio_event_id}
                                   channels={eventHolds?.allChannelKeysIncludingNoChannel}
                                   objectColor={holdObjectColor}
                                   objectIcon={holdObjectIcon}
                                   popoverInfo={(object) => getHoldPopoverInfo(object, eventHolds) || ''}
                                   onRenderStarted={
                                     chart => {
                                       seatsIOChartChanged(chart);
                                       chart.config.session = "start";
                                       chart.config.onObjectSelected = (object, selectedTicketType) => seatsIOObjectSelected(object, selectedTicketType);
                                       chart.config.onObjectDeselected = (object, deselectedTicketType) => seatsIOObjectDeselected(object, deselectedTicketType);
                                       chart.config.multiSelectEnabled = true;
                                     }
                                   }
                                   priceFormatter={
                                     price => formatter.format(price)
                                   }
                                   colorScheme={colorScheme}
                                   region="na" />
            </div>
          </div>
        </div>
      ) : (
        <div className="row">
          <div className="col-12">
            <div className="card border-0 h-100"
                 style={{"background": "var(--od-c-f9f9f9, #f9f9f9)"}}>
              <div className="card-body d-flex align-items-center justify-content-center">
                <div className="text-center">
                  <p className="mb-2"
                     style={{"fontSize": "16px"}}>
                    <strong>An error occurred.</strong>
                  </p>
                  <p className="mb-0">
                    Please try again later.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

ChooseSeatedTicketTypes.propTypes = {
  team: PropTypes.object.isRequired,
  csrfToken: PropTypes.string.isRequired,
  confirm: PropTypes.object.isRequired,
  ticketTypes: PropTypes.array.isRequired,
  ticketTypesChanged: PropTypes.func.isRequired,
  addOns: PropTypes.array,
  seatsIOPublicKey: PropTypes.string.isRequired,
  seatsIOChart: PropTypes.object,
  seatsIOChartChanged: PropTypes.func.isRequired,
  seatsIOSelectedObjects: PropTypes.array,
  seatsIOSelectedObjectsChanged: PropTypes.func.isRequired,
  selectedObjectLabel: PropTypes.func.isRequired,
  selectedObjectType: PropTypes.func.isRequired,
  buildTicketReservation: PropTypes.func.isRequired,
  orderType: PropTypes.string,
  eventHolds: PropTypes.object,
};

export default ChooseSeatedTicketTypes;

